import { useState } from "react";
import { ExpandMore, ExpandLess, Delete, Add, ContentCopy } from "@mui/icons-material";
import QuestionForm from "./QuestionForm";
import { deleteRequest, showMessage } from "../../../../config";

const SectionForm = ({
    section,
    index,
    moduleKey,
    isExpanded = false,
    isDeletePending = false,
    onToggleExpand,
    onUpdate,
    onRemove,
    onDeleteQuestion,
    onClone,
    errors = {}
}) => {
    const [expandedSubFolderKey, setExpandedSubFolderKey] = useState(null);
    const [isSectionQuestionExpanded, setIsSectionQuestionExpanded] = useState(false);
    const [expandedSubFolderQuestions, setExpandedSubFolderQuestions] = useState({});
    const [expandedSubSubQuestions, setExpandedSubSubQuestions] = useState({});
    const [expandedSubFolderExtraQuestions, setExpandedSubFolderExtraQuestions] = useState({});
    const [expandedSubSubSectionKey, setExpandedSubSubSectionKey] = useState({});

    const handleSectionChange = (field, value) => {
        onUpdate({ ...section, [field]: value });
    };

    const handleQuestionUpdate = (updatedQuestion) => {
        handleSectionChange('question', updatedQuestion);
    };

    const removeQuestionWithApi = async (question, applyLocalClear) => {
        const questionId = question?._id;
        if (onDeleteQuestion) {
            const ok = await onDeleteQuestion(questionId);
            if (!ok) return;
        }
        applyLocalClear(null);
    };

    const addSubFolder = () => {
        const subFolders = section.subFolders || {};
        const subFolderKeys = Object.keys(subFolders);
        const nextKey = String(subFolderKeys.length + 1);
        const subFolderNo = `${section.sectionNo}.${nextKey}`;

        const newSubFolder = {
            name: '',
            no: subFolderNo,
            question: {
                qNo: subFolderNo, // Question number matches sub-folder number (e.g., UMS1.1)
                qDescription: '',
                questionType: '',
                customOptions: [{ value: '', info: '' }],
                predefinedOptionsInfo: {},
                dateInfo: '',
                tooltip: '',
                toolboxOffer: '',
                riskDescription: '',
                recommendDescription: '',
                riskScores: {}
            }
        };

        const updatedSubFolders = {
            ...subFolders,
            [nextKey]: newSubFolder
        };

        handleSectionChange('subFolders', updatedSubFolders);
        setExpandedSubFolderKey(nextKey); // Expand the new sub-folder
    };

    const updateSubFolder = (key, updatedSubFolder) => {
        const updatedSubFolders = {
            ...section.subFolders,
            [key]: updatedSubFolder
        };
        handleSectionChange('subFolders', updatedSubFolders);
    };

    const addSubSubFolder = (parentKey) => {
        const subFolders = section.subFolders || {};
        const parent = subFolders[parentKey];
        if (!parent) return;

        const existingSubSubFolders = parent.subFolders || {};
        const subSubKeys = Object.keys(existingSubSubFolders);
        const nextKey = String(subSubKeys.length + 1);
        const subSubNo = `${parent.no}.${nextKey}`;

        const newSubSubFolder = {
            name: '',
            no: subSubNo,
            question: {
                qNo: subSubNo,
                qDescription: '',
                questionType: '',
                customOptions: [{ value: '', info: '' }],
                predefinedOptionsInfo: {},
                dateInfo: '',
                tooltip: '',
                toolboxOffer: '',
                riskDescription: '',
                recommendDescription: '',
                riskScores: {}
            }
        };

        const updatedParent = {
            ...parent,
            subFolders: {
                ...existingSubSubFolders,
                [nextKey]: newSubSubFolder
            }
        };

        updateSubFolder(parentKey, updatedParent);
        setExpandedSubSubSectionKey(prev => ({ ...prev, [parentKey]: nextKey }));
    };

    const updateSubSubFolder = (parentKey, subKey, updatedSubSubFolder) => {
        const subFolders = section.subFolders || {};
        const parent = subFolders[parentKey];
        if (!parent) return;

        const existingSubSubFolders = parent.subFolders || {};
        const updatedSubSubFolders = {
            ...existingSubSubFolders,
            [subKey]: updatedSubSubFolder
        };

        const updatedParent = {
            ...parent,
            subFolders: updatedSubSubFolders
        };

        updateSubFolder(parentKey, updatedParent);
    };

    const removeSubSubFolder = (parentKey, subKey) => {
        const subFolders = section.subFolders || {};
        const parent = subFolders[parentKey];
        if (!parent || !parent.subFolders) return;

        const updatedSubSubFolders = { ...parent.subFolders };
        delete updatedSubSubFolders[subKey];

        const reindexedSubSubFolders = {};
        const oldSubSubKeys = Object.keys(updatedSubSubFolders).sort((a, b) => Number(a) - Number(b));
        oldSubSubKeys.forEach((oldKey, newIdx) => {
            const newKey = String(newIdx + 1);
            const subFolder = updatedSubSubFolders[oldKey];
            const newNo = `${parent.no}.${newKey}`;

            reindexedSubSubFolders[newKey] = {
                ...subFolder,
                no: newNo,
                question: subFolder.question ? {
                    ...subFolder.question,
                    qNo: newNo
                } : subFolder.question
            };
        });

        const updatedParent = {
            ...parent,
            subFolders: reindexedSubSubFolders
        };

        updateSubFolder(parentKey, updatedParent);

        // Re-key expanded sub-sub question state so it stays in sync after reindex
        const newExpandedSubSub = { ...expandedSubSubQuestions };
        oldSubSubKeys.forEach((oldKey) => {
            delete newExpandedSubSub[`${parentKey}-${oldKey}`];
        });
        oldSubSubKeys.forEach((oldKey, idx) => {
            const newKey = String(idx + 1);
            newExpandedSubSub[`${parentKey}-${newKey}`] = expandedSubSubQuestions[`${parentKey}-${oldKey}`] ?? false;
        });
        setExpandedSubSubQuestions(newExpandedSubSub);

        // Update expanded sub-sub-section key: if we removed the expanded one, clear it; else map to new key
        const currentExpanded = expandedSubSubSectionKey[parentKey];
        if (currentExpanded === subKey) {
            setExpandedSubSubSectionKey(prev => ({ ...prev, [parentKey]: undefined }));
        } else if (currentExpanded) {
            const idx = oldSubSubKeys.indexOf(currentExpanded);
            if (idx !== -1) {
                const newKey = String(idx + 1);
                setExpandedSubSubSectionKey(prev => ({ ...prev, [parentKey]: newKey }));
            }
        }
    };

    const collectQuestionIdsFromSubFolderTree = (subFolderNode) => {
        const ids = [];
        if (!subFolderNode || typeof subFolderNode !== 'object') return ids;

        if (subFolderNode.question && subFolderNode.question._id) {
            ids.push(String(subFolderNode.question._id));
        }

        if (Array.isArray(subFolderNode.questions)) {
            subFolderNode.questions.forEach((q) => {
                if (q && q._id) ids.push(String(q._id));
            });
        }

        const nested = subFolderNode.subFolders;
        if (nested && typeof nested === 'object') {
            Object.values(nested).forEach((child) => {
                ids.push(...collectQuestionIdsFromSubFolderTree(child));
            });
        }
        return ids;
    };

    const removeSubFolder = async (key) => {
        const currentSubFolders = section.subFolders || {};
        const subFolderToDelete = currentSubFolders[key];
        if (!subFolderToDelete) return;

        const subFolderNo = subFolderToDelete.no;
        const sectionId = section._id;

        // If the section is already persisted, use the dedicated API which atomically
        // removes the subFolder from the section document and deletes all related
        // TempQuestion (and TempAnswer) records scoped to this exact section variant.
        if (sectionId && subFolderNo) {
            const response = await deleteRequest(`/modules/temp-sections/${sectionId}/subfolder`, { subFolderNo });
            if (response?.code !== 200) {
                showMessage('Failed to delete sub-folder. Please try again.');
                return;
            }
        } else {
            // Section not yet saved — delete questions individually via the existing callback
            const questionIds = [...new Set(collectQuestionIdsFromSubFolderTree(subFolderToDelete).filter(Boolean))];
            if (onDeleteQuestion && questionIds.length > 0) {
                const results = await Promise.all(questionIds.map((questionId) => onDeleteQuestion(questionId)));
                if (results.some((ok) => !ok)) return;
            }
        }
        const updatedSubFolders = { ...currentSubFolders };
        delete updatedSubFolders[key];
        handleSectionChange('subFolders', updatedSubFolders);
        // Drop deleted key from expanded state
        const nextExpandedQuestions = { ...expandedSubFolderQuestions };
        delete nextExpandedQuestions[key];
        setExpandedSubFolderQuestions(nextExpandedQuestions);
        if (expandedSubFolderKey === key) {
            setExpandedSubFolderKey(null);
        }
    };

    const sectionDisplay = `Section ${section.sectionNo || `${moduleKey}${index + 1}`}`;

    return (
        <div className="bg1 br12px mb1-5rem" style={{
            border: '1px solid #e8e8e8',
            transition: 'all 0.3s ease',
            minWidth: 0,
            maxWidth: '100%'
        }}>
            {/* Section Header */}
            <div className="df jcsb aic" style={{
                padding: '1.5rem 2rem',
                background: isExpanded ? 'linear-gradient(135deg, #4CAF5015 0%, #2196F315 100%)' : 'white',
                borderBottom: isExpanded ? '1px solid #e8e8e8' : 'none',
                transition: 'all 0.3s ease'
            }}>
                <div className="df aic flex-1" style={{ gap: '12px', minWidth: 0 }}>
                    {/* Expand/Collapse Button */}
                    <button
                        type="button"
                        onClick={onToggleExpand}
                        className="df aic jcc cp"
                        style={{
                            border: 'none',
                            background: isExpanded ? '#4CAF50' : '#f5f5f5',
                            color: isExpanded ? 'white' : '#666',
                            width: '36px',
                            height: '36px',
                            borderRadius: '8px',
                            transition: 'all 0.2s ease',
                            padding: 0
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.background = isExpanded ? '#45a049' : '#e8e8e8';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.background = isExpanded ? '#4CAF50' : '#f5f5f5';
                        }}
                    >
                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </button>

                    {/* Section Title */}
                    <div className="flex-1" style={{ minWidth: 0 }}>
                        <h4 className="fs1rem fw600 mb0-25rem" style={{ color: '#2c3e50', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {section.sectionName || `Section ${index + 1}`}
                        </h4>
                        <p className="fs0-85rem" style={{ color: '#999', margin: 0 }}>
                            {sectionDisplay}
                        </p>
                    </div>
                </div>

                <div className="df jcfe">
                    {/* Clone Button */}
                    {onClone && (
                        <button
                            type="button"
                            onClick={onClone}
                            className="df aic jcc cp ml0-5rem"
                            style={{
                                border: 'none',
                                background: '#fff',
                                color: '#2196F3',
                                width: '36px',
                                height: '36px',
                                borderRadius: '8px',
                                transition: 'all 0.2s ease'
                            }}
                            onMouseOver={(e) => {
                                e.currentTarget.style.background = '#2196F315';
                            }}
                            onMouseOut={(e) => {
                                e.currentTarget.style.background = '#fff';
                            }}
                            title="Clone section to another tier"
                        >
                            <ContentCopy style={{ fontSize: '20px' }} />
                        </button>
                    )}
                    {/* Delete Button */}
                    <button
                        type="button"
                        onClick={onRemove}
                        disabled={isDeletePending}
                        className="df aic jcc cp ml0-5rem"
                        style={{
                            border: 'none',
                            background: isDeletePending ? '#f3f3f3' : '#fff',
                            color: '#dc3545',
                            width: '36px',
                            height: '36px',
                            borderRadius: '8px',
                            transition: 'all 0.2s ease',
                            opacity: isDeletePending ? 0.6 : 1,
                            cursor: isDeletePending ? 'not-allowed' : 'pointer',
                        }}
                        onMouseOver={(e) => {
                            if (isDeletePending) return;
                            e.currentTarget.style.background = '#dc3545';
                            e.currentTarget.style.color = 'white';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.background = isDeletePending ? '#f3f3f3' : '#fff';
                            e.currentTarget.style.color = '#dc3545';
                        }}
                        title={isDeletePending ? 'Delete queued (3s)' : 'Delete section'}
                    >
                        <Delete style={{ fontSize: '20px' }} />
                    </button>
                </div>
            </div>

            {/* Section Content (Question + Sub-Folders) */}
            {isExpanded && (
                <div className="p2rem" style={{ background: '#fafafa' }}>
                    {/* Section Name Input */}
                    <div className="mb1-5rem">
                        <label className="fs0-875rem fw600 mb0-75rem" style={{ color: '#555', display: 'block' }}>
                            Section Name <span style={{ color: '#dc3545' }}>*</span>
                        </label>
                        <input
                            type="text"
                            value={section.sectionName || ''}
                            onChange={(e) => handleSectionChange('sectionName', e.target.value)}
                            placeholder="Enter section name..."
                            className="w100"
                            style={{
                                boxSizing: 'border-box',
                                border: errors.sectionName ? '2px solid #dc3545' : '1px solid #ddd',
                                borderRadius: '8px',
                                padding: '10px 12px',
                                fontSize: '0.95rem',
                                outline: 'none',
                                transition: 'all 0.2s ease',
                                background: 'white'
                            }}
                            onFocus={(e) => e.target.style.borderColor = '#4CAF50'}
                            onBlur={(e) => e.target.style.borderColor = errors.sectionName ? '#dc3545' : '#ddd'}
                        />
                        {errors.sectionName && (
                            <small className="fs11px mt0-5rem" style={{ color: '#dc3545', display: 'block' }}>
                                {errors.sectionName}
                            </small>
                        )}
                    </div>

                    {/* Single Question */}
                    {section.question && (
                        <div className="mb1-5rem">
                            <QuestionForm
                                question={section.question || {}}
                                index={0}
                                moduleKey={moduleKey}
                                sectionIndex={index}
                                subfolderPath={[]}
                                isExpanded={isSectionQuestionExpanded}
                                onToggleExpand={() => setIsSectionQuestionExpanded(prev => !prev)}
                                onUpdate={handleQuestionUpdate}
                                onRemove={() =>
                                    removeQuestionWithApi(
                                        section.question,
                                        (clearedQuestion) => handleSectionChange('question', clearedQuestion)
                                    )
                                }
                                errors={errors.question || {}}
                                hideBorder={true}
                                hideExpand={false}
                                hideDelete={false}
                            />
                        </div>
                    )}

                    {/* Add Sub-Folder Button */}
                    <button
                        type="button"
                        onClick={addSubFolder}
                        className="df aic jcc cp w100 mb1-5rem"
                        style={{
                            border: '2px dashed #2196F3',
                            background: 'white',
                            color: '#2196F3',
                            padding: '12px',
                            borderRadius: '8px',
                            fontSize: '0.95rem',
                            fontWeight: '600',
                            gap: '8px',
                            transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.background = '#2196F315';
                            e.currentTarget.style.borderColor = '#1976D2';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.background = 'white';
                            e.currentTarget.style.borderColor = '#2196F3';
                        }}
                    >
                        <Add style={{ fontSize: '20px' }} />
                        Add Sub-Section
                    </button>

                    {/* Sub-Folders */}
                    {section.subFolders && Object.keys(section.subFolders).length > 0 && (
                        <div className="df fdc" style={{ gap: '1rem' }}>
                            {Object.keys(section.subFolders).sort((a, b) => Number(a) - Number(b)).map((key) => {
                                const subFolder = section.subFolders[key];
                                const isSubFolderExpanded = expandedSubFolderKey === key;
                                const isSubFolderQuestionExpanded = expandedSubFolderQuestions[key] ?? false;

                                return (
                                    <div key={key} className="bg1 br10px" style={{
                                        border: '1px solid #e0e0e0',
                                        marginLeft: '1.5rem',
                                        marginRight: '1.5rem',
                                        transition: 'all 0.3s ease'
                                    }}>
                                        {/* Sub-Folder Header */}
                                        <div className="df jcsb aic" style={{
                                            padding: '1rem 1.5rem',
                                            background: isSubFolderExpanded ? '#f8f9fa' : 'white',
                                            borderBottom: isSubFolderExpanded ? '1px solid #e0e0e0' : 'none',
                                            transition: 'all 0.3s ease'
                                        }}>
                                            <div className="df aic flex-1" style={{ gap: '12px', minWidth: 0 }}>
                                                {/* Expand/Collapse Button */}
                                                <button
                                                    type="button"
                                                    onClick={() => setExpandedSubFolderKey(isSubFolderExpanded ? null : key)}
                                                    className="df aic jcc cp"
                                                    style={{
                                                        border: 'none',
                                                        background: isSubFolderExpanded ? '#2196F3' : '#f5f5f5',
                                                        color: isSubFolderExpanded ? 'white' : '#666',
                                                        width: '32px',
                                                        height: '32px',
                                                        borderRadius: '6px',
                                                        transition: 'all 0.2s ease',
                                                        padding: 0
                                                    }}
                                                >
                                                    {isSubFolderExpanded ? <ExpandLess /> : <ExpandMore />}
                                                </button>

                                                {/* Sub-Folder Title */}
                                                <div className="flex-1">
                                                    <h5 className="fs0-95rem fw600" style={{ color: '#2c3e50', margin: 0 }}>
                                                        {subFolder.name || `Sub-Folder ${key}`}
                                                    </h5>
                                                    <p className="fs0-8rem" style={{ color: '#999', margin: 0 }}>
                                                        {subFolder.no}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* Delete Button */}
                                            <button
                                                type="button"
                                                onClick={() => removeSubFolder(key)}
                                                className="df aic jcc cp ml1rem"
                                                style={{
                                                    border: 'none',
                                                    background: '#fff',
                                                    color: '#dc3545',
                                                    width: '32px',
                                                    height: '32px',
                                                    borderRadius: '6px',
                                                    transition: 'all 0.2s ease'
                                                }}
                                                onMouseOver={(e) => {
                                                    e.currentTarget.style.background = '#dc3545';
                                                    e.currentTarget.style.color = 'white';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.currentTarget.style.background = '#fff';
                                                    e.currentTarget.style.color = '#dc3545';
                                                }}
                                            >
                                                <Delete style={{ fontSize: '18px' }} />
                                            </button>
                                        </div>

                                        {/* Sub-Folder Content */}
                                        {isSubFolderExpanded && (
                                            <div className="p1-5rem" style={{ background: '#fafafa' }}>
                                                {/* Sub-Folder Name Input */}
                                                <div className="mb1-5rem">
                                                    <label className="fs0-875rem fw500 mb0-5rem" style={{ color: '#555', display: 'block' }}>
                                                        Sub-Folder Name
                                                    </label>
                                                    <input
                                                        type="text"
                                                        value={subFolder.name || ''}
                                                        onChange={(e) => updateSubFolder(key, { ...subFolder, name: e.target.value })}
                                                        placeholder="Enter sub-folder name..."
                                                        className="w100"
                                                        style={{
                                                            border: errors.subFolders?.[key]?.name ? '2px solid #dc3545' : '1px solid #ddd',
                                                            borderRadius: '8px',
                                                            padding: '10px 12px',
                                                            fontSize: '0.95rem',
                                                            outline: 'none',
                                                            transition: 'all 0.2s ease',
                                                            background: 'white'
                                                        }}
                                                        onFocus={(e) => e.target.style.borderColor = '#2196F3'}
                                                        onBlur={(e) => e.target.style.borderColor = errors.subFolders?.[key]?.name ? '#dc3545' : '#ddd'}
                                                    />
                                                    {errors.subFolders?.[key]?.name && (
                                                        <small className="fs11px mt0-5rem" style={{ color: '#dc3545', display: 'block' }}>
                                                            {errors.subFolders[key].name}
                                                        </small>
                                                    )}
                                                </div>

                                                {/* Sub-Folder Question */}
                                                {subFolder.question && (
                                                    <QuestionForm
                                                        question={subFolder.question || {}}
                                                        index={0}
                                                        moduleKey={moduleKey}
                                                        sectionIndex={index}
                                                        subfolderPath={[]}
                                                        isExpanded={isSubFolderQuestionExpanded}
                                                        onToggleExpand={() =>
                                                            setExpandedSubFolderQuestions(prev => ({
                                                                ...prev,
                                                                [key]: !prev[key]
                                                            }))
                                                        }
                                                        onUpdate={(updatedQuestion) => updateSubFolder(key, { ...subFolder, question: updatedQuestion })}
                                                        onRemove={() =>
                                                            removeQuestionWithApi(
                                                                subFolder.question,
                                                                (clearedQuestion) => updateSubFolder(key, { ...subFolder, question: clearedQuestion })
                                                            )
                                                        }
                                                        errors={errors.subFolders?.[key]?.question || {}}
                                                        hideBorder={true}
                                                        hideExpand={false}
                                                        hideDelete={false}
                                                    />
                                                )}
                                                {Array.isArray(subFolder.questions) && subFolder.questions.length > 0 && (
                                                    <div className="df fdc" style={{ gap: '1rem', marginTop: '1rem' }}>
                                                        {subFolder.questions.map((extraQ, qIdx) => {
                                                            const expKey = `${key}-extra-${qIdx}`;
                                                            return (
                                                                <QuestionForm
                                                                    key={extraQ._id || expKey}
                                                                    question={extraQ || {}}
                                                                    index={qIdx + 1}
                                                                    moduleKey={moduleKey}
                                                                    sectionIndex={index}
                                                                    subfolderPath={[]}
                                                                    isExpanded={expandedSubFolderExtraQuestions[expKey] ?? true}
                                                                    onToggleExpand={() =>
                                                                        setExpandedSubFolderExtraQuestions((prev) => ({
                                                                            ...prev,
                                                                            [expKey]: !(prev[expKey] ?? true),
                                                                        }))
                                                                    }
                                                                    onUpdate={(updatedQuestion) => {
                                                                        const next = [...(subFolder.questions || [])];
                                                                        next[qIdx] = updatedQuestion;
                                                                        updateSubFolder(key, { ...subFolder, questions: next });
                                                                    }}
                                                                    onRemove={() =>
                                                                        removeQuestionWithApi(extraQ, () => {
                                                                            const next = (subFolder.questions || []).filter(
                                                                                (_, i) => i !== qIdx
                                                                            );
                                                                            updateSubFolder(key, { ...subFolder, questions: next });
                                                                        })
                                                                    }
                                                                    errors={errors.subFolders?.[key]?.questions?.[qIdx] || {}}
                                                                    hideBorder={true}
                                                                    hideExpand={false}
                                                                    hideDelete={false}
                                                                />
                                                            );
                                                        })}
                                                    </div>
                                                )}
                                                {/* Sub-Sub-Sections */}
                                                <div className="mt1rem" style={{ marginLeft: '1.5rem' }}>
                                                    <button
                                                        type="button"
                                                        onClick={() => addSubSubFolder(key)}
                                                        className="df aic jcc cp w100 mb1rem"
                                                        style={{
                                                            border: '2px dashed #9c27b0',
                                                            background: 'white',
                                                            color: '#9c27b0',
                                                            padding: '10px',
                                                            borderRadius: '8px',
                                                            fontSize: '0.85rem',
                                                            fontWeight: '600',
                                                            gap: '8px',
                                                            transition: 'all 0.2s ease'
                                                        }}
                                                    >
                                                        <Add style={{ fontSize: '18px' }} />
                                                        Add Sub-Sub-Section
                                                    </button>

                                                    {subFolder.subFolders && Object.keys(subFolder.subFolders).length > 0 && (
                                                        <div className="df fdc" style={{ gap: '0.75rem' }}>
                                                            {Object.keys(subFolder.subFolders)
                                                                .sort((a, b) => Number(a) - Number(b))
                                                                .map((subKey) => {
                                                                    const subSub = subFolder.subFolders[subKey];
                                                                    const isSubSubExpanded = expandedSubSubSectionKey[key] === subKey;
                                                                    return (
                                                                        <div
                                                                            key={subKey}
                                                                            className="bg1 br10px"
                                                                            style={{
                                                                                border: '1px solid #e0e0e0',
                                                                                marginLeft: '1.5rem',
                                                                                marginRight: '1.5rem',
                                                                                transition: 'all 0.3s ease'
                                                                            }}
                                                                        >
                                                                            {/* Sub-Sub-Section Header - same pattern as Sub-Folder */}
                                                                            <div className="df jcsb aic" style={{
                                                                                padding: '0.875rem 1.25rem',
                                                                                background: isSubSubExpanded ? '#f5f0f8' : 'white',
                                                                                borderBottom: isSubSubExpanded ? '1px solid #e0e0e0' : 'none',
                                                                                transition: 'all 0.3s ease'
                                                                            }}>
                                                                                <div className="df aic flex-1" style={{ gap: '10px' }}>
                                                                                    <button
                                                                                        type="button"
                                                                                        onClick={() => setExpandedSubSubSectionKey(prev => ({
                                                                                            ...prev,
                                                                                            [key]: prev[key] === subKey ? undefined : subKey
                                                                                        }))}
                                                                                        className="df aic jcc cp"
                                                                                        style={{
                                                                                            border: 'none',
                                                                                            background: isSubSubExpanded ? '#9c27b0' : '#f5f5f5',
                                                                                            color: isSubSubExpanded ? 'white' : '#666',
                                                                                            width: '30px',
                                                                                            height: '30px',
                                                                                            borderRadius: '6px',
                                                                                            transition: 'all 0.2s ease',
                                                                                            padding: 0
                                                                                        }}
                                                                                    >
                                                                                        {isSubSubExpanded ? <ExpandLess /> : <ExpandMore />}
                                                                                    </button>
                                                                                    <div className="flex-1" style={{ minWidth: 0 }}>
                                                                                        <h6 className="fs0-9rem fw600" style={{ color: '#2c3e50', margin: 0 }}>
                                                                                            {subSub.name || `Sub-Sub-Section ${subKey}`}
                                                                                        </h6>
                                                                                        <p className="fs0-75rem" style={{ color: '#999', margin: 0 }}>
                                                                                            {subSub.no}
                                                                                        </p>
                                                                                    </div>
                                                                                </div>
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() => removeSubSubFolder(key, subKey)}
                                                                                    className="df aic jcc cp ml0-75rem"
                                                                                    style={{
                                                                                        border: 'none',
                                                                                        background: '#fff',
                                                                                        color: '#dc3545',
                                                                                        width: '30px',
                                                                                        height: '30px',
                                                                                        borderRadius: '6px',
                                                                                        transition: 'all 0.2s ease'
                                                                                    }}
                                                                                    onMouseOver={(e) => {
                                                                                        e.currentTarget.style.background = '#dc3545';
                                                                                        e.currentTarget.style.color = 'white';
                                                                                    }}
                                                                                    onMouseOut={(e) => {
                                                                                        e.currentTarget.style.background = '#fff';
                                                                                        e.currentTarget.style.color = '#dc3545';
                                                                                    }}
                                                                                    title="Remove sub-sub-section"
                                                                                >
                                                                                    <Delete style={{ fontSize: '16px' }} />
                                                                                </button>
                                                                            </div>

                                                                            {/* Sub-Sub-Section Content - same pattern as Sub-Folder */}
                                                                            {isSubSubExpanded && (
                                                                                <div className="p1-25rem" style={{ background: '#fafafa' }}>
                                                                                    <div className="mb1-5rem">
                                                                                        <label className="fs0-875rem fw500 mb0-5rem" style={{ color: '#555', display: 'block' }}>
                                                                                            Sub-Sub-Section Name <span style={{ color: '#dc3545' }}>*</span>
                                                                                        </label>
                                                                                        <input
                                                                                            type="text"
                                                                                            value={subSub.name || ''}
                                                                                            onChange={(e) =>
                                                                                                updateSubSubFolder(key, subKey, {
                                                                                                    ...subSub,
                                                                                                    name: e.target.value
                                                                                                })
                                                                                            }
                                                                                            placeholder="Enter sub-sub-section name..."
                                                                                            className="w100"
                                                                                            style={{
                                                                                                border: errors.subFolders?.[key]?.subFolders?.[subKey]?.name ? '2px solid #dc3545' : '1px solid #ddd',
                                                                                                borderRadius: '8px',
                                                                                                padding: '10px 12px',
                                                                                                fontSize: '0.95rem',
                                                                                                outline: 'none',
                                                                                                transition: 'all 0.2s ease',
                                                                                                background: 'white'
                                                                                            }}
                                                                                            onFocus={(e) => e.target.style.borderColor = '#9c27b0'}
                                                                                            onBlur={(e) => e.target.style.borderColor = errors.subFolders?.[key]?.subFolders?.[subKey]?.name ? '#dc3545' : '#ddd'}
                                                                                        />
                                                                                        {errors.subFolders?.[key]?.subFolders?.[subKey]?.name && (
                                                                                            <small className="fs11px mt0-5rem" style={{ color: '#dc3545', display: 'block' }}>
                                                                                                {errors.subFolders[key].subFolders[subKey].name}
                                                                                            </small>
                                                                                        )}
                                                                                    </div>

                                                                                    {subSub.question && (
                                                                                        <QuestionForm
                                                                                            question={subSub.question || {}}
                                                                                            index={0}
                                                                                            moduleKey={moduleKey}
                                                                                            sectionIndex={index}
                                                                                            subfolderPath={[]}
                                                                                            isExpanded={expandedSubSubQuestions[`${key}-${subKey}`] ?? false}
                                                                                            onToggleExpand={() =>
                                                                                                setExpandedSubSubQuestions(prev => ({
                                                                                                    ...prev,
                                                                                                    [`${key}-${subKey}`]: !(prev[`${key}-${subKey}`] ?? false)
                                                                                                }))
                                                                                            }
                                                                                            onUpdate={(updatedQuestion) =>
                                                                                                updateSubSubFolder(key, subKey, {
                                                                                                    ...subSub,
                                                                                                    question: updatedQuestion
                                                                                                })
                                                                                            }
                                                                                            onRemove={() =>
                                                                                                removeQuestionWithApi(
                                                                                                    subSub.question,
                                                                                                    (clearedQuestion) =>
                                                                                                        updateSubSubFolder(key, subKey, {
                                                                                                            ...subSub,
                                                                                                            question: clearedQuestion
                                                                                                        })
                                                                                                )
                                                                                            }
                                                                                            errors={errors.subFolders?.[key]?.subFolders?.[subKey]?.question || {}}
                                                                                            hideBorder={true}
                                                                                            hideExpand={false}
                                                                                            hideDelete={false}
                                                                                        />
                                                                                    )}
                                                                                </div>
                                                                            )}
                                                                        </div>
                                                                    );
                                                                })}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SectionForm;
